import { useCallback, useEffect, useRef, useState } from 'react'
import { runtimeApi } from '@/app/services/runtimeApi'

/** Hard client-side cap for kanban / group-by / calendar data sets. */
export const CAPPED_FETCH_LIMIT = 1000
const PAGE_LIMIT = 500

export interface CappedDateFilter {
  field: string
  from: string
  to: string
}

interface UseCappedRowsOptions {
  connectionId: string
  objectName: string
  enabled: boolean
  search: string
  sortField: string
  sortDir: 'asc' | 'desc'
  showDeleted: boolean
  dateFilter?: CappedDateFilter
  /** Whitelisted equality filters (schema tabs/filter_fields), merged server-side with `search`. */
  filters?: Record<string, unknown>
  /** Attribute names `search` should OR-match across (schema `search_fields`). */
  searchFields?: string[]
  /** Active i18next locale — resolves translatable reference display server-side. */
  locale?: string
}

export interface CappedRowsApi {
  rows: Record<string, unknown>[]
  total: number
  loading: boolean
  error: string | null
  capped: boolean
  reload: () => void
  mutateRow: (id: string, patch: Record<string, unknown>) => void
  /** Drop one row without a refetch (optimistic delete / permanent purge). */
  removeRow: (id: string) => void
}

export function useCappedRows(opts: UseCappedRowsOptions): CappedRowsApi {
  const { connectionId, objectName, enabled, search, sortField, sortDir, showDeleted, dateFilter, filters, searchFields, locale } = opts
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)
  const generation = useRef(0)

  const dfField = dateFilter?.field ?? ''
  const dfFrom = dateFilter?.from ?? ''
  const dfTo = dateFilter?.to ?? ''
  const filtersKey = JSON.stringify(filters ?? {})
  const searchFieldsKey = (searchFields ?? []).join(',')

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return undefined
    }
    const gen = ++generation.current
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const acc: Record<string, unknown>[] = []
        let totalCount = 0
        let page = 1
        for (;;) {
          const resp = await runtimeApi.list(connectionId, objectName, {
            page,
            limit: PAGE_LIMIT,
            search: search || undefined,
            sort_field: sortField || undefined,
            sort_dir: sortDir,
            include_deleted: showDeleted || undefined,
            filter_date_field: dfField || undefined,
            date_from: dfFrom || undefined,
            date_to: dfTo || undefined,
            filters,
            search_fields: searchFields,
            locale,
          })
          if (gen !== generation.current) return
          totalCount = resp.meta.total
          acc.push(...resp.data)
          if (acc.length >= totalCount || acc.length >= CAPPED_FETCH_LIMIT || resp.data.length === 0) break
          page += 1
        }
        setRows(acc.slice(0, CAPPED_FETCH_LIMIT))
        setTotal(totalCount)
        setLoading(false)
      } catch (e) {
        if (gen !== generation.current) return
        setError(e instanceof Error ? e.message : 'Failed to load records')
        setLoading(false)
      }
    })()
    return () => {
      generation.current += 1
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, objectName, enabled, search, sortField, sortDir, showDeleted, dfField, dfFrom, dfTo, filtersKey, searchFieldsKey, locale, reloadTick])

  const reload = useCallback(() => setReloadTick((t) => t + 1), [])

  const mutateRow = useCallback((id: string, patch: Record<string, unknown>) => {
    setRows((prev) => prev.map((r) => (String(r.id) === String(id) ? { ...r, ...patch } : r)))
  }, [])

  const removeRow = useCallback((id: string) => {
    setRows((prev) => {
      if (!prev.some((r) => String(r.id) === String(id))) return prev
      setTotal((t) => Math.max(0, t - 1))
      return prev.filter((r) => String(r.id) !== String(id))
    })
  }, [])

  return { rows, total, loading, error, capped: total > CAPPED_FETCH_LIMIT, reload, mutateRow, removeRow }
}
